import { Check, Loader2, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";

export type AdminToastKind = "success" | "error" | "info";

export type AdminToast = {
  id: string;
  message: string;
  kind: AdminToastKind;
};

export function AdminToastStack({
  toasts,
  onDismiss,
}: {
  toasts: AdminToast[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2 px-4">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={[
            "admin-toast-enter pointer-events-auto flex items-start gap-2 rounded-xl border px-3 py-2.5 text-sm shadow-lg",
            t.kind === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : t.kind === "error"
                ? "border-red-200 bg-red-50 text-red-900"
                : "border-black/10 bg-white text-ink",
          ].join(" ")}
        >
          {t.kind === "success" ? (
            <Check className="mt-0.5 h-4 w-4 shrink-0" />
          ) : t.kind === "error" ? (
            <X className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
          )}
          <span className="flex-1">{t.message}</span>
          <button
            type="button"
            className="text-xs opacity-60 hover:opacity-100"
            onClick={() => onDismiss(t.id)}
          >
            Dismiss
          </button>
        </div>
      ))}
    </div>
  );
}

export function useAdminToasts() {
  const [toasts, setToasts] = useState<AdminToast[]>([]);

  function push(message: string, kind: AdminToastKind = "success") {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, message, kind }]);
    return id;
  }

  function dismiss(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  useEffect(() => {
    if (!toasts.length) return;
    const timers = toasts.map((t) =>
      window.setTimeout(() => dismiss(t.id), t.kind === "error" ? 6000 : 3500),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [toasts]);

  return { toasts, push, dismiss };
}

export function AdminBusyOverlay({ show, label }: { show: boolean; label?: string }) {
  if (!show) return null;
  return (
    <div className="admin-busy-overlay fixed inset-0 z-[55] flex items-center justify-center bg-black/10 backdrop-blur-[1px]">
      <div className="admin-pop-in flex items-center gap-2 rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm shadow-lg">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        {label ?? "Saving…"}
      </div>
    </div>
  );
}

export function actionIcon(kind: "save" | "delete" | "update") {
  if (kind === "delete") return Trash2;
  return Check;
}
